import React from 'react';
import { useRememberedScroll } from '../../hooks/useRemembered';
import { BarChart3, CalendarClock, Euro, Gauge, RefreshCw, Ruler, Wallet } from 'lucide-react';
import { Button } from '../Button';
import { ErpBereich, useErpCockpit } from '../../hooks/useErpCockpit';
import { AuftragsbestandCard } from './AuftragsbestandCard';
import { NachkalkulationCard } from './NachkalkulationCard';
import { KapazitaetCard } from './KapazitaetCard';
import { LiquiditaetCard } from './LiquiditaetCard';
import { AuftragswerteCard } from './AuftragswerteCard';
import { EffortRatesCard } from './EffortRatesCard';

// Container der ERP-Steuerung (ERP/CRM-Plan Abschnitt 8 — Phase 5).
//
// Duenner Container (Standing Decision): er haelt nur die Bereichswahl und
// reicht die Ladezustaende aus `useErpCockpit` an die sechs Karten durch. Jede
// Zahl kommt aus den puren Funktionen in services/erpSteering.ts, jedes Laden
// aus services/erpSteeringService.ts.
//
// Vier Auswertungen und zwei Pflege-Ansichten:
//   Auftragsbestand  — Auftragswert, fakturiert, offen, Reichweite
//   Nachkalkulation  — Deckungsbeitrag je Projekt/Kunde/Branche/Region
//   Kapazitaet       — Begehungs-Soll gegen Koepfe, Ampel je Monat/Region
//   Liquiditaet      — 13-Wochen-Vorschau
//   Auftragswerte    — Pflege von project_order_values
//   Saetze           — Pflege von effort_rates
//
// Ohne die Migration Nr. 23 (supabase/erp_steering.sql) melden die Karten
// einen Ladefehler und bleiben leer; der Rest der App ist unberuehrt.

interface Props {
    /** Sprung in die Projektansicht aus den Tabellen. Optional. */
    onOpenProject?: (projectId: string) => void;
}

const BEREICHE: { key: ErpBereich; label: string; icon: React.ReactNode }[] = [
    { key: 'auftragsbestand', label: 'Auftragsbestand', icon: <BarChart3 className="w-4 h-4" /> },
    { key: 'nachkalkulation', label: 'Nachkalkulation', icon: <Euro className="w-4 h-4" /> },
    { key: 'kapazitaet', label: 'Kapazität', icon: <CalendarClock className="w-4 h-4" /> },
    { key: 'liquiditaet', label: 'Liquidität', icon: <Wallet className="w-4 h-4" /> },
    { key: 'auftragswerte', label: 'Auftragswerte', icon: <Ruler className="w-4 h-4" /> },
    { key: 'saetze', label: 'Sätze', icon: <Gauge className="w-4 h-4" /> },
];

export const ErpCockpit: React.FC<Props> = ({ onOpenProject }) => {
    // Ein Stichtag für die ganze Ansicht: zwei Karten, die sich um eine
    // Mitternacht unterscheiden, wären nicht erklärbar.
    const heute = React.useMemo(() => new Date().toISOString().slice(0, 10), []);
    const c = useErpCockpit(heute);
    const scrollRef = useRememberedScroll(`erp.${c.bereich}.scroll`);

    return (
        <div ref={scrollRef} data-glass-view="steuerung" className="h-full overflow-y-auto">
            <div className="space-y-6 max-w-7xl mx-auto animate-fade-in p-4 md:p-6 pb-20">
                {/* ── Bereichswahl ── */}
                <div data-glass="reiter" className="inline-flex flex-wrap rounded-xl border border-slate-200 bg-white overflow-hidden shadow-sm">
                    {BEREICHE.map((b) => (
                        <button
                            key={b.key}
                            type="button"
                            onClick={() => c.setBereich(b.key)}
                            aria-pressed={c.bereich === b.key}
                            className={`flex items-center gap-2 px-5 py-2.5 text-sm font-bold whitespace-nowrap border-b-2 transition-all ${
                                c.bereich === b.key
                                    ? 'border-blue-600 text-blue-600 bg-blue-50/50'
                                    : 'border-transparent text-slate-400 hover:text-slate-600 hover:bg-slate-50'
                            }`}
                        >
                            {b.icon}
                            {b.label}
                        </button>
                    ))}
                </div>

                {/* ── Kopf ── */}
                <div data-glass="kopf" className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
                    <div className="flex items-center gap-4">
                        <div className="p-3 bg-blue-50 text-blue-600 rounded-2xl">
                            <Gauge className="w-8 h-8" />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-slate-800">Steuerung</h2>
                            <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">
                                Auftragsbestand · Nachkalkulation · Kapazität · Liquidität
                            </p>
                        </div>
                    </div>
                    <Button variant="secondary" onClick={c.neuLaden} icon={<RefreshCw className="w-4 h-4" />}>
                        Aktualisieren
                    </Button>
                </div>

                <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4 md:p-6">
                    {c.bereich === 'auftragsbestand' && (
                        <AuftragsbestandCard zustand={c.bestand} onOpenProject={onOpenProject} />
                    )}
                    {c.bereich === 'nachkalkulation' && (
                        <NachkalkulationCard
                            zustand={c.nachkalk}
                            kunden={c.kunden}
                            filter={c.filter}
                            onFilter={c.setFilter}
                            onAnwenden={c.neuLaden}
                            onOpenProject={onOpenProject}
                        />
                    )}
                    {c.bereich === 'kapazitaet' && (
                        <KapazitaetCard
                            zustand={c.kapazitaet}
                            einstellungen={c.einstellungen}
                            onEinstellungen={c.setEinstellungen}
                            onAnwenden={c.neuLaden}
                            warnungen={c.kapazitaetsWarnungen}
                        />
                    )}
                    {c.bereich === 'liquiditaet' && <LiquiditaetCard zustand={c.liquiditaet} />}
                    {c.bereich === 'auftragswerte' && (
                        <AuftragswerteCard zustand={c.auftragswerte} onGeaendert={c.neuLaden} />
                    )}
                    {c.bereich === 'saetze' && <EffortRatesCard zustand={c.saetze} onGeaendert={c.neuLaden} />}
                </div>
            </div>
        </div>
    );
};
